import { useEffect, useRef, useState } from 'react';

import {
  type CredentialOptionsResponse,
  EMPTY_CREDENTIAL_OPTIONS_RESPONSE,
  getCredentialOptions,
} from 'lib/api/getCredentialOptions';
import { QuotingPortals } from 'types/enums/Integrations';

const PORTAL_VALUES = Object.values(QuotingPortals);

// Debounce for customer name before batching portal credential GETs (scope/recommendation).
export const PORTAL_CREDENTIAL_OPTIONS_DEBOUNCE_MS = 400;

export interface UsePortalCredentialOptionsBatchArgs {
  enabled: boolean;
  portals?: QuotingPortals[];
  tmsCustomerId?: string;
  customerName?: string;
}

/**
 * Single batched fetch of GET /integrations/credentials/options for every bidding portal.
 * Used for submit-bid gating and {@link SubmitBidSection} so we do not duplicate requests.
 */
export function usePortalCredentialOptionsBatch({
  enabled,
  portals = PORTAL_VALUES,
  tmsCustomerId,
  customerName,
}: UsePortalCredentialOptionsBatchArgs): {
  credentialOptionsByPortal: Partial<Record<string, CredentialOptionsResponse>>;
  isPortalCredentialBatchLoading: boolean;
} {
  const [debouncedCustomerName, setDebouncedCustomerName] =
    useState(customerName);
  const debouncedCustomerNameInitRef = useRef(true);
  const [credentialOptionsByPortal, setCredentialOptionsByPortal] = useState<
    Partial<Record<string, CredentialOptionsResponse>>
  >({});
  const [isPortalCredentialBatchLoading, setIsPortalCredentialBatchLoading] =
    useState(enabled);

  useEffect(() => {
    if (debouncedCustomerNameInitRef.current) {
      debouncedCustomerNameInitRef.current = false;
      setDebouncedCustomerName(customerName);
      return;
    }

    const handle = window.setTimeout(() => {
      setDebouncedCustomerName(customerName);
    }, PORTAL_CREDENTIAL_OPTIONS_DEBOUNCE_MS);

    return () => window.clearTimeout(handle);
  }, [customerName]);

  useEffect(() => {
    if (!enabled) {
      setCredentialOptionsByPortal({});
      setIsPortalCredentialBatchLoading(false);
      return;
    }

    let isCancelled = false;

    const run = async () => {
      setIsPortalCredentialBatchLoading(true);

      const results = await Promise.all(
        portals.map(async (portal) => {
          const result = await getCredentialOptions({
            integrationName: portal,
            tmsCustomerId,
            customerName: debouncedCustomerName,
          });
          return [portal, result] as const;
        })
      );

      if (isCancelled) {
        return;
      }

      const next: Partial<Record<string, CredentialOptionsResponse>> = {};
      for (const [portal, result] of results) {
        next[portal] = result.isOk()
          ? result.value
          : EMPTY_CREDENTIAL_OPTIONS_RESPONSE;
      }
      setCredentialOptionsByPortal(next);
      setIsPortalCredentialBatchLoading(false);
    };

    void run();

    return () => {
      isCancelled = true;
    };
  }, [enabled, portals, tmsCustomerId, debouncedCustomerName]);

  return {
    credentialOptionsByPortal,
    isPortalCredentialBatchLoading,
  };
}
